import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  MapPinIcon,
  WifiIcon,
  DevicePhoneMobileIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';

const RESULT_COLORS: Record<string, string> = {
  up_to_date: 'text-green-400',
  updates_available: 'text-amber-400',
  check_failed: 'text-red-400',
};

const RESULT_ICONS: Record<string, typeof CheckCircleIcon> = {
  up_to_date: CheckCircleIcon,
  updates_available: ExclamationTriangleIcon,
  check_failed: XCircleIcon,
};

const ACTION_COLORS: Record<string, string> = {
  none: 'text-slate-500',
  update: 'text-amber-400',
  install: 'text-cyan-400',
  skip: 'text-slate-500',
};

function formatRelative(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatTimestamp(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-NZ');
}

function formatCoord(val: number, type: 'lat' | 'lon'): string {
  const abs = Math.abs(val).toFixed(5);
  if (type === 'lat') return `${abs}\u00B0 ${val >= 0 ? 'N' : 'S'}`;
  return `${abs}\u00B0 ${val >= 0 ? 'E' : 'W'}`;
}

function shortPackage(pkg: string): string {
  if (!pkg) return '\u2014';
  const parts = pkg.split('.');
  return parts.length > 2 ? parts.slice(-1)[0] : pkg;
}

function openGoogleMaps(lat: number, lon: number) {
  window.open(`https://www.google.com/maps?q=${lat},${lon}&z=12`, '_blank');
}

// Kiosk diagnostics live on Admin → Devices → specific device → Health (PR #214).
// AdminUpdatesPage stays a compact activity log and only offers a navigation
// link to the device's detail page; no kiosk_state rendering happens here.

function UpdateDetailRow({ entry }: { entry: any }) {
  const apps = Array.isArray(entry.appDetails) ? entry.appDetails : [];
  const meta = entry.deviceMeta && typeof entry.deviceMeta === 'object' ? entry.deviceMeta : {};
  const hasLocation = entry.latitude != null && entry.longitude != null;
  const hasMeta = Object.keys(meta).length > 0;

  return (
    <tr className="border-t border-slate-700/30 bg-slate-800/50">
      <td colSpan={5} className="px-4 py-4">
        <div className="space-y-4">
          {/* Error Message */}
          {entry.errorMessage && (
            <div className="rounded-lg border border-red-800/50 bg-red-900/20 p-3">
              <div className="text-xs text-red-400 font-medium mb-1">Error</div>
              <div className="text-sm font-mono text-red-200">{entry.errorMessage}</div>
            </div>
          )}

          {/* Location */}
          {hasLocation && (
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Location</div>
              <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-3 flex items-center gap-4">
                <div className="flex items-center gap-4 text-sm font-mono flex-1">
                  <span>
                    <span className="text-slate-500 text-xs">Lat</span>
                    <span className="text-slate-300 ml-1">{formatCoord(entry.latitude, 'lat')}</span>
                  </span>
                  <span>
                    <span className="text-slate-500 text-xs">Lon</span>
                    <span className="text-slate-300 ml-1">{formatCoord(entry.longitude, 'lon')}</span>
                  </span>
                  {entry.gpsAccuracy != null && (
                    <span>
                      <span className="text-slate-500 text-xs">Accuracy</span>
                      <span className="text-slate-300 ml-1">{entry.gpsAccuracy.toFixed(1)} m</span>
                    </span>
                  )}
                </div>
                <button
                  onClick={() => openGoogleMaps(entry.latitude, entry.longitude)}
                  className="flex items-center gap-1.5 rounded-lg bg-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-600 transition-colors"
                >
                  <MapPinIcon className="h-3.5 w-3.5" />
                  View Map
                </button>
              </div>
            </div>
          )}

          {/* App Details Table */}
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">App Details</div>
            <div className="rounded-lg border border-slate-700 bg-slate-900/50 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-slate-500">
                    <th className="px-3 py-2 text-left font-medium">App</th>
                    <th className="px-3 py-2 text-left font-medium">Installed</th>
                    <th className="px-3 py-2 text-left font-medium">Available</th>
                    <th className="px-3 py-2 text-left font-medium">Action</th>
                    <th className="px-3 py-2 text-left font-medium">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {apps.map((app: any, i: number) => (
                    <tr key={app.package || i} className="border-t border-slate-700/50">
                      <td className="px-3 py-2 text-xs font-mono text-cyan-400">{shortPackage(app.package)}</td>
                      <td className="px-3 py-2 text-xs font-mono text-slate-300">
                        {app.installed_version || '\u2014'}
                        {app.installed_version_code ? ` (${app.installed_version_code})` : ''}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-slate-300">
                        {app.available_version || '\u2014'}
                        {app.available_version_code ? ` (${app.available_version_code})` : ''}
                      </td>
                      <td className={`px-3 py-2 text-xs ${ACTION_COLORS[app.action] ?? 'text-slate-400'}`}>
                        {app.action || '\u2014'}
                      </td>
                      <td className="px-3 py-2 text-xs text-red-400">{app.error || ''}</td>
                    </tr>
                  ))}
                  {apps.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-4 text-xs text-slate-500 text-center">
                        No app details recorded
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Device Meta */}
          {hasMeta && (
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Device Info</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {meta.launcher_version && (
                  <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-2.5">
                    <div className="text-xs text-slate-500">Launcher</div>
                    <div className="text-xs font-mono text-slate-300">{meta.launcher_version}</div>
                  </div>
                )}
                {meta.android_version && (
                  <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-2.5">
                    <div className="text-xs text-slate-500">Android</div>
                    <div className="text-xs font-mono text-slate-300">{meta.android_version}</div>
                  </div>
                )}
                {meta.device_model && (
                  <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-2.5">
                    <div className="text-xs text-slate-500 flex items-center gap-1">
                      <DevicePhoneMobileIcon className="h-3 w-3" /> Device
                    </div>
                    <div className="text-xs font-mono text-slate-300">{meta.device_model}</div>
                  </div>
                )}
                <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-2.5">
                  <div className="text-xs text-slate-500 flex items-center gap-1">
                    <WifiIcon className="h-3 w-3" /> WiFi
                  </div>
                  <div className={`text-xs font-mono ${meta.wifi_connected ? 'text-green-400' : 'text-red-400'}`}>
                    {meta.wifi_connected ? (meta.wifi_ssid || 'Connected') : 'Disconnected'}
                  </div>
                </div>
                <div className="rounded-lg border border-slate-700 bg-slate-900/50 p-2.5">
                  <div className="text-xs text-slate-500 flex items-center gap-1">
                    <SignalIcon className="h-3 w-3" /> Cellular
                  </div>
                  <div className={`text-xs font-mono ${meta.cellular_connected ? 'text-green-400' : 'text-slate-500'}`}>
                    {meta.cellular_connected ? 'Connected' : 'Not connected'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Timestamp + direct link to the device's detail page. Kiosk
              diagnostics are intentionally NOT rendered here — they live on
              Admin → Devices → specific device → Health. `entry.deviceId`
              maps directly to /admin/devices/:deviceId (deviceUpdateLogs.deviceId
              has a foreign key to devices.id, so it is route-safe). */}
          <div className="text-xs text-slate-500 pt-2 border-t border-slate-700/50 flex flex-wrap items-center gap-3">
            <span>Checked: {formatTimestamp(entry.checkedAt)}</span>
            {entry.deviceId && (
              <>
                <span>
                  Device: <span className="font-mono">{entry.deviceId}</span>
                </span>
                <Link
                  to={`/admin/devices/${entry.deviceId}`}
                  className="ml-auto inline-flex items-center gap-1 rounded-md border border-cyan-700/40 bg-cyan-900/20 px-2 py-0.5 text-[11px] text-cyan-300 hover:bg-cyan-900/30 hover:text-cyan-200"
                  title="Open this device's detail page"
                >
                  <ArrowTopRightOnSquareIcon className="h-3 w-3" />
                  Open device tab
                </Link>
              </>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

export function AdminUpdatesPage() {
  const [page, setPage] = useState(1);
  const [resultFilter, setResultFilter] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const limit = 50;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'neptune', 'updates', page, resultFilter],
    queryFn: () =>
      api.adminGetNeptuneUpdates({
        page,
        limit,
        result: resultFilter || undefined,
      }),
  });

  const totalPages = Math.ceil((data?.total ?? 0) / limit);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-white">Update Checks</h1>
          <p className="text-sm text-slate-400">
            OTA update check history from devices{data ? ` \u2014 ${data.total.toLocaleString()} total` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/updates/live"
            className="flex items-center gap-2 rounded-lg bg-emerald-700 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-600"
            title="Watch update requests and check-ins as they happen, per Scout"
          >
            <SignalIcon className="h-4 w-4" />
            Live monitor
          </Link>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 rounded-lg bg-slate-700 px-3 py-2 text-sm text-slate-300 hover:bg-slate-600"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['', 'up_to_date', 'updates_available', 'check_failed'].map((val) => (
          <button
            key={val}
            onClick={() => { setResultFilter(val); setPage(1); }}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
              resultFilter === val
                ? 'bg-slate-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            {val === '' ? 'All' : val.replace(/_/g, ' ')}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <ArrowPathIcon className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      )}

      {isError && (
        <div className="rounded-lg border border-red-800 bg-red-900/20 p-4 text-sm text-red-400">
          Failed to load update logs.{' '}
          <button className="underline" onClick={() => refetch()}>Retry</button>
        </div>
      )}

      {data && (
        <>
          <div className="overflow-x-auto rounded-lg border border-slate-700">
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-800/50 text-xs text-slate-400">
                <tr>
                  <th className="px-4 py-3 font-medium">Vessel</th>
                  <th className="px-4 py-3 font-medium">Device ID</th>
                  <th className="px-4 py-3 font-medium">Result</th>
                  <th className="px-4 py-3 font-medium">Apps</th>
                  <th className="px-4 py-3 font-medium">Checked</th>
                </tr>
              </thead>
              <tbody>
                {data.updates.map((entry: any) => {
                  const Icon = RESULT_ICONS[entry.result] ?? ExclamationTriangleIcon;
                  const isExpanded = expandedId === entry.id;
                  const appCount = Array.isArray(entry.appDetails) ? entry.appDetails.length : 0;
                  return (
                    <>
                      <tr
                        key={entry.id}
                        className={`border-t border-slate-700/50 cursor-pointer transition-colors ${
                          isExpanded ? 'bg-slate-700/40' : 'hover:bg-slate-700/30'
                        }`}
                        onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                      >
                        <td className="px-4 py-3 text-slate-200 font-medium">
                          {entry.vesselName || '\u2014'}
                        </td>
                        <td className="px-4 py-3 text-slate-400 font-mono text-xs">
                          {entry.deviceId ? `${entry.deviceId.slice(0, 8)}...${entry.deviceId.slice(-4)}` : '\u2014'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`flex items-center gap-1.5 ${RESULT_COLORS[entry.result] ?? 'text-slate-400'}`}>
                            <Icon className="h-4 w-4" />
                            {entry.result.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-400 text-xs">
                          {appCount > 0 ? `${appCount} apps` : '\u2014'}
                        </td>
                        <td className="px-4 py-3 text-slate-400 whitespace-nowrap">
                          {formatRelative(entry.checkedAt)}
                        </td>
                      </tr>
                      {isExpanded && <UpdateDetailRow key={`${entry.id}-detail`} entry={entry} />}
                    </>
                  );
                })}
                {data.updates.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                      No update logs found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-slate-400">
              <span>
                Page {page} of {totalPages}
              </span>
              <div className="flex gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                  className="rounded bg-slate-700 px-3 py-1 disabled:opacity-30 hover:bg-slate-600"
                >
                  Previous
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className="rounded bg-slate-700 px-3 py-1 disabled:opacity-30 hover:bg-slate-600"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
